import { useState, useEffect, useCallback } from 'react';
import { getUserAvatar, getUserAvatarImageUrl, UserAvatarData } from '../services/userAvatar.service';

export interface UseUserAvatarResult {
  avatar: UserAvatarData | null;
  avatarImageUrl: string | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook para gerenciar o carregamento do avatar do usuário do novo sistema avatar-konva
 * @param userId ID do usuário para buscar o avatar
 * @param autoFetch Se deve buscar automaticamente o avatar ao montar o componente
 */
export function useUserAvatar(userId: string | null | undefined, autoFetch = true): UseUserAvatarResult {
  const [avatar, setAvatar] = useState<UserAvatarData | null>(null);
  const [avatarImageUrl, setAvatarImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvatar = useCallback(async (userIdToFetch: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const avatarData = await getUserAvatar(userIdToFetch);
      
      if (avatarData) {
        setAvatar(avatarData);
        setAvatarImageUrl(avatarData.avatarImageUrl);
      } else {
        setAvatar(null);
        setAvatarImageUrl(null);
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao carregar avatar';
      setError(errorMessage);
      setAvatar(null);
      setAvatarImageUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refetch = useCallback(() => {
    if (userId) {
      fetchAvatar(userId);
    }
  }, [userId, fetchAvatar]);

  // Efeito para carregar o avatar automaticamente
  useEffect(() => {
    if (autoFetch && userId) {
      fetchAvatar(userId);
    }
  }, [userId, autoFetch, fetchAvatar]);

  return {
    avatar,
    avatarImageUrl,
    isLoading,
    error,
    refetch
  };
}

/**
 * Hook simplificado que retorna apenas a URL da imagem do avatar
 * @param userId ID do usuário para buscar o avatar
 */
export function useUserAvatarImageUrl(userId: string | null | undefined): {
  avatarImageUrl: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [avatarImageUrl, setAvatarImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setAvatarImageUrl(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchAvatarUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const url = await getUserAvatarImageUrl(userId);
        
        if (url) {
          // Adiciona cache busting para garantir que a imagem mais recente seja carregada
          const urlWithCacheBusting = url.includes('?') 
            ? `${url}&t=${Date.now()}` 
            : `${url}?t=${Date.now()}`;
          setAvatarImageUrl(urlWithCacheBusting);
        } else {
          setAvatarImageUrl(null);
        }
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar URL do avatar';
        setError(errorMessage);
        setAvatarImageUrl(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatarUrl();
  }, [userId]);

  return {
    avatarImageUrl,
    isLoading,
    error
  };
}
